import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ClipboardCheck, Sparkles, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PostureChip } from '@/components/ui/posture-chip';
import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { ApiError } from '@/lib/api/client';
import {
  useCheckpointAssessmentsQuery,
  useAssessmentDetailQuery,
  useStartAssessmentRoundMutation,
  usePatchScoresMutation,
  useSubmitAssessmentMutation,
  useRequestScoreCandidatesMutation,
  type Checkpoint,
  type ScoreCandidate,
  type PatchScoreItem,
} from '@/lib/api/checkpoints';
import { titleCase } from '@/lib/utils/format';
import { toSentenceCase } from '@/lib/utils/ai-text';
import { LinkedDeliverablesList } from '@/components/contract/linked-deliverables';
import { cn } from '@/lib/utils/cn';
import { pluralize } from '@/lib/contract-labels';

/**
 * Vendor scoring round for an IV&V checkpoint (Tier 2 — T-0067).
 *
 * Flow per the assessment state machine:
 *   1. No in-progress round → "Start round N" (POST /assessments).
 *   2. In-progress round → score each criterion by its score_type
 *      (numeric 0-100 / pass-fail / red-yellow-green), optional findings +
 *      recommendations, saved via PATCH /scores.
 *   3. Submit (lead) — the API rejects incomplete rounds; the button stays
 *      disabled until every criterion has a score, and the result posture
 *      chip renders on success.
 */
export function CheckpointScoringModal({
  checkpoint,
  open,
  onOpenChange,
}: {
  checkpoint: Checkpoint;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const assessments = useCheckpointAssessmentsQuery(open ? checkpoint.checkpoint_id : undefined);
  const inProgress = (assessments.data?.items ?? []).find((a) => a.status === 'in_progress');
  const latestSubmitted = (assessments.data?.items ?? [])
    .filter((a) => a.status === 'submitted')
    .sort((a, b) => b.round_seq - a.round_seq)[0];

  const detail = useAssessmentDetailQuery(inProgress?.assessment_id);

  const [values, setValues] = useState<Record<string, string>>({});
  const [findings, setFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [error, setError] = useState<string | null>(null);

  const start = useStartAssessmentRoundMutation(checkpoint.checkpoint_id);
  const patch = usePatchScoresMutation(checkpoint.checkpoint_id);
  const submit = useSubmitAssessmentMutation(checkpoint.checkpoint_id);
  const askAi = useRequestScoreCandidatesMutation();

  // Seed local form state from the persisted round ONCE per round. The
  // detail query refetches in the background (live refresh, e.g. when AI
  // suggestions land); re-seeding on every refetch would wipe whatever the
  // consultant has typed but not saved yet.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!detail.data) return;
    if (seededFor.current === detail.data.assessment_id) return;
    seededFor.current = detail.data.assessment_id;
    const seeded: Record<string, string> = {};
    for (const s of detail.data.scores) seeded[s.criterion_id] = String(s.score_value);
    setValues(seeded);
    setFindings(detail.data.findings ?? '');
    setRecommendations(detail.data.recommendations ?? '');
  }, [detail.data]);

  // AI suggestions for this round, by criterion. A suggestion is evidence
  // plus a proposed verdict; the score is only what the consultant saves.
  const candidates = useMemo(() => {
    const map = new Map<string, ScoreCandidate>();
    for (const c of detail.data?.ai_candidates ?? []) map.set(c.criterion_id, c);
    return map;
  }, [detail.data?.ai_candidates]);

  /** Fill every still-unscored categorical criterion with its AI suggestion. */
  function applyAllSuggestions() {
    setValues((prev) => {
      const next = { ...prev };
      for (const c of checkpoint.criteria) {
        const cand = candidates.get(c.criterion_id);
        if (cand?.suggested_value && (next[c.criterion_id] ?? '').trim() === '') {
          next[c.criterion_id] = cand.suggested_value;
        }
      }
      return next;
    });
  }
  const fillableCount = checkpoint.criteria.filter((c) => {
    const cand = candidates.get(c.criterion_id);
    return Boolean(cand?.suggested_value) && (values[c.criterion_id] ?? '').trim() === '';
  }).length;

  const scoredCount = useMemo(
    () =>
      checkpoint.criteria.filter((c) => (values[c.criterion_id] ?? '').toString().trim() !== '')
        .length,
    [checkpoint.criteria, values],
  );
  const allScored = scoredCount === checkpoint.criteria.length;

  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Action failed — try again.');

  function buildScores(): PatchScoreItem[] {
    const out: PatchScoreItem[] = [];
    for (const c of checkpoint.criteria) {
      const raw = (values[c.criterion_id] ?? '').toString().trim();
      if (raw === '') continue;
      // Saved value equals the AI suggestion → record it as AI-confirmed.
      const cand = candidates.get(c.criterion_id);
      out.push({
        criterion_id: c.criterion_id,
        score_value: c.score_type === 'numeric' ? Number(raw) : raw,
        ...(cand?.suggested_value && cand.suggested_value === raw
          ? { from_ai_proposal_id: cand.ai_proposal_id }
          : {}),
      });
    }
    return out;
  }

  function saveScores(thenSubmit: boolean) {
    if (!inProgress) return;
    setError(null);
    patch.mutate(
      {
        assessmentId: inProgress.assessment_id,
        scores: buildScores(),
        findings: findings.trim() ? findings.trim() : null,
        recommendations: recommendations.trim() ? recommendations.trim() : null,
      },
      {
        onSuccess: () => {
          if (thenSubmit) {
            submit.mutate(
              { assessmentId: inProgress.assessment_id, idempotencyKey: crypto.randomUUID() },
              { onSuccess: () => onOpenChange(false), onError },
            );
          }
        },
        onError,
      },
    );
  }

  const pending = start.isPending || patch.isPending || submit.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(680px,94vw)] max-h-[92vh] -translate-x-1/2 -translate-y-1/2',
            'grid grid-rows-[auto,1fr,auto] overflow-hidden rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-meta uppercase tracking-wide text-ink-500">
                <ClipboardCheck className="h-3 w-3" aria-hidden="true" />
                Vendor scoring
              </p>
              <Dialog.Title className="mt-1 text-h2 font-semibold text-ink-900">
                {checkpoint.name}
              </Dialog.Title>
              <p className="mt-1 text-meta text-ink-500">
                {inProgress
                  ? `Round ${inProgress.round_seq} in progress · ${scoredCount} of ${pluralize(checkpoint.criteria.length, 'criterion', 'criteria')} scored`
                  : latestSubmitted
                    ? `Last round ${latestSubmitted.round_seq} submitted`
                    : 'No scoring rounds yet'}
              </p>
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>

          <div className="space-y-4 overflow-y-auto px-5 py-4">
            {assessments.isLoading ? (
              <div className="flex items-center gap-2 text-body-sm text-ink-500">
                <Spinner /> Loading rounds…
              </div>
            ) : !inProgress ? (
              <div className="space-y-3">
                {latestSubmitted?.overall_posture ? (
                  <div className="flex items-center gap-2 text-body-sm text-ink-700">
                    <span>Latest submitted posture:</span>
                    <PostureChip
                      status={
                        latestSubmitted.overall_posture === 'green'
                          ? 'healthy'
                          : latestSubmitted.overall_posture === 'red'
                            ? 'critical'
                            : latestSubmitted.overall_posture === 'yellow_divergent'
                              ? 'at-risk'
                              : 'unassessed'
                      }
                    >
                      {titleCase(latestSubmitted.overall_posture)}
                    </PostureChip>
                  </div>
                ) : null}
                <p className="text-body-sm text-ink-700">
                  Start a scoring round to record vendor performance against this checkpoint&apos;s{' '}
                  {pluralize(checkpoint.criteria.length, 'criterion', 'criteria')}.
                </p>
                <Button
                  type="button"
                  size="sm"
                  disabled={pending || checkpoint.status !== 'active'}
                  onClick={() => {
                    setError(null);
                    start.mutate({ idempotencyKey: crypto.randomUUID() }, { onError });
                  }}
                >
                  {start.isPending ? <Spinner /> : null}
                  Start round {(latestSubmitted?.round_seq ?? 0) + 1}
                </Button>
                {checkpoint.status !== 'active' ? (
                  <p className="text-meta text-ink-500">
                    This checkpoint is archived — new rounds can&apos;t be started.
                  </p>
                ) : null}
              </div>
            ) : (
              <>
                <AiSurface className="flex flex-wrap items-center justify-between gap-2 rounded-card px-3 py-2 text-meta">
                  <span className="inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                    {candidates.size > 0
                      ? `AI read the evidence documents and has a suggestion for ${candidates.size} of ${pluralize(checkpoint.criteria.length, 'criterion', 'criteria')}. You decide each score.`
                      : askAi.isSuccess
                        ? 'AI is reading the evidence documents. Suggestions appear here when ready.'
                        : 'No AI suggestions yet. They appear here once the evidence documents have been read. You can score without them.'}
                  </span>
                  <span className="flex items-center gap-2">
                    {fillableCount > 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={applyAllSuggestions}
                      >
                        Use {fillableCount} suggestion{fillableCount === 1 ? '' : 's'}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={askAi.isPending}
                      onClick={() => inProgress && askAi.mutate(inProgress.assessment_id)}
                      title="Re-read the evidence, for example after uploading a new report"
                    >
                      {candidates.size > 0 ? 'Ask AI again' : 'Ask AI'}
                    </Button>
                  </span>
                </AiSurface>
                {detail.data?.linked_deliverables && detail.data.linked_deliverables.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-meta font-semibold uppercase tracking-wider text-ink-500">
                      Deliverables this checkpoint depends on
                    </p>
                    <LinkedDeliverablesList
                      items={detail.data.linked_deliverables}
                      engagementId={checkpoint.engagement_id}
                    />
                  </div>
                ) : null}
                <ul className="space-y-2">
                  {checkpoint.criteria.map((c) => (
                    <li
                      key={c.criterion_id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ink-100 bg-paper-0 p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-body-sm font-medium text-ink-900">{c.label}</p>
                        <p className="text-meta text-ink-500">
                          {c.score_type === 'numeric'
                            ? 'Score 0–100'
                            : c.score_type === 'pass_fail'
                              ? 'Pass / Fail'
                              : 'Red / Yellow / Green'}{' '}
                          · weight {c.weight}
                        </p>
                      </div>
                      {c.score_type === 'numeric' ? (
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={values[c.criterion_id] ?? ''}
                          onChange={(e) =>
                            setValues((prev) => ({ ...prev, [c.criterion_id]: e.target.value }))
                          }
                          className="w-24 rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900"
                        />
                      ) : (
                        <div className="flex gap-1.5">
                          {(c.score_type === 'pass_fail'
                            ? ['pass', 'fail']
                            : ['green', 'yellow', 'red']
                          ).map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() =>
                                setValues((prev) => ({ ...prev, [c.criterion_id]: opt }))
                              }
                              className={cn(
                                'rounded-card border px-2.5 py-1.5 text-meta font-medium',
                                values[c.criterion_id] === opt
                                  ? 'border-accent-amber-600 bg-accent-amber-50 text-ink-900'
                                  : 'border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-50',
                              )}
                            >
                              {titleCase(opt)}
                            </button>
                          ))}
                        </div>
                      )}
                      {candidates.get(c.criterion_id) ? (
                        <CandidateNote
                          candidate={candidates.get(c.criterion_id)!}
                          currentValue={values[c.criterion_id] ?? ''}
                          onUse={(v) => setValues((prev) => ({ ...prev, [c.criterion_id]: v }))}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>

                <label className="block text-meta font-medium text-ink-700">
                  Findings
                  <textarea
                    value={findings}
                    onChange={(e) => setFindings(e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900"
                  />
                </label>
                <label className="block text-meta font-medium text-ink-700">
                  Recommendations
                  <textarea
                    value={recommendations}
                    onChange={(e) => setRecommendations(e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900"
                  />
                </label>
              </>
            )}

            {error ? (
              <p className="rounded-card border border-cite-broken bg-paper-0 p-3 text-meta font-medium text-cite-broken">
                {error}
              </p>
            ) : null}
          </div>

          {inProgress ? (
            <div className="flex items-center justify-between gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
              <p className="text-meta text-ink-500">
                {allScored
                  ? 'All criteria scored — ready to submit.'
                  : `${pluralize(checkpoint.criteria.length - scoredCount, 'criterion', 'criteria')} still unscored; submit unlocks when all are done.`}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pending}
                  onClick={() => saveScores(false)}
                >
                  {patch.isPending && !submit.isPending ? <Spinner /> : null}
                  Save draft
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={pending || !allScored}
                  onClick={() => saveScores(true)}
                >
                  {submit.isPending ? <Spinner /> : null}
                  Submit round
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t border-ink-100 bg-paper-50 px-5 py-3" />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const ASSESSMENT_LABEL: Record<string, string> = {
  met: 'evidence shows this is met',
  partially_met: 'evidence shows this is partially met',
  not_met: 'evidence shows this is not met',
};

/**
 * The AI's suggestion for one criterion, shown under its score control:
 * what it suggests (or, for a numeric criterion, how the evidence reads —
 * never a number), why, how confident, and which passages it read.
 * "Use AI value" only fills the control; saving is still the consultant's.
 */
function CandidateNote({
  candidate,
  currentValue,
  onUse,
}: {
  candidate: ScoreCandidate;
  currentValue: string;
  onUse: (value: string) => void;
}) {
  const suggested = candidate.suggested_value;
  const reading = ASSESSMENT_LABEL[candidate.assessment] ?? candidate.assessment;
  return (
    <AiSurface className="w-full rounded-card px-3 py-2 text-meta">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex flex-wrap items-center gap-2 font-medium">
          {suggested ? (
            <>
              AI suggests <span className="font-semibold">{titleCase(suggested)}</span>
            </>
          ) : (
            <>AI reading: {reading}. You choose the score.</>
          )}
          <AiConfidenceChip value={candidate.confidence} />
        </span>
        {suggested ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={currentValue === suggested}
            onClick={() => onUse(suggested)}
          >
            {currentValue === suggested ? 'Using AI value' : 'Use AI value'}
          </Button>
        ) : null}
      </div>
      {/* AI text is shown in sentence case; the stored rationale is unchanged (C2-33). */}
      <p className="mt-1 text-ink-700">{toSentenceCase(candidate.rationale)}</p>
      {candidate.citations.length > 0 ? (
        <p className="mt-1 text-ink-500">
          Evidence:{' '}
          {candidate.citations
            .slice(0, 3)
            .map(
              (ci) =>
                `${ci.document_title}${ci.page_number !== null ? `, p.${ci.page_number}` : ''}`,
            )
            .join(' · ')}
          {candidate.citations.length > 3 ? ` · +${candidate.citations.length - 3} more` : ''}
        </p>
      ) : null}
    </AiSurface>
  );
}
